import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CreateLeagueWeekRequest } from '../../../models/createleagueweekrequest';
import { UpdateLeagueWeekRequest } from '../../../models/updateleagueweekrequest';
import { LeagueWeek } from '../../../models/LeagueWeek';
import { FormsModule } from '@angular/forms';
import { NgIf } from '@angular/common';
import { GolfEventService } from '../../../services/golf-event.service';
import { toCalendarControlFriendlyDate } from '../../../util/dateutil';
import { ApplicationService } from '../../../services/application.service';
import { EVENT_FORMAT } from '../../../models/constants';

@Component({
  selector: 'app-create-week',
  standalone: true,
  imports: [FormsModule, NgIf],
  templateUrl: './create-week.component.html',
  styleUrl: './create-week.component.css'
})
export class CreateWeekComponent implements OnChanges {
  constructor(private golfEventService:GolfEventService, private applicationService:ApplicationService) {}

  @Input() golfEventId:number|null = null;
  @Input() formatType:string = 'MULTI_WEEK_LEAGUE';
  @Input() eventStartDate:string|null = null;
  @Input() eventEndDate:string|null = null;
  @Input() weekToEdit:LeagueWeek|null = null;
  @Output() saved = new EventEmitter<void>();
  @Output() canceled = new EventEmitter<void>();

  createLeagueWeekRequest:CreateLeagueWeekRequest = this.emptyRequest();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weekToEdit'] && this.weekToEdit) {
      this.createLeagueWeekRequest = {
        gimmeCircleSize: this.weekToEdit.gimmeCircleSize,
        golfCourseName: this.weekToEdit.golfCourseName,
        holesToPlay: this.weekToEdit.holesToPlay,
        pinLocations: this.weekToEdit.pinLocations,
        teeLocation: this.weekToEdit.teeLocation,
        weekEndDate: this.toInputDate(this.weekToEdit.weekEndDate),
        weekStartDate: this.toInputDate(this.weekToEdit.weekStartDate),
        golfEventId: this.weekToEdit.golfEventId,
        roundsToCount: this.weekToEdit.roundsToCount,
        maxRoundsToPost: this.weekToEdit.maxRoundsToPost,
        coursePar: this.weekToEdit.coursePar
      };
    }
  }

  async save() {
    if(!this.golfEventId && !this.weekToEdit) return;

    this.createLeagueWeekRequest.roundsToCount = Number(this.createLeagueWeekRequest.roundsToCount);
    this.createLeagueWeekRequest.maxRoundsToPost = Number(this.createLeagueWeekRequest.maxRoundsToPost);
    this.createLeagueWeekRequest.coursePar = Number(this.createLeagueWeekRequest.coursePar);
    this.createLeagueWeekRequest.gimmeCircleSize = Number(this.createLeagueWeekRequest.gimmeCircleSize);

    if (!this.isLeagueWeek && this.eventStartDate && this.eventEndDate) {
      this.createLeagueWeekRequest.weekStartDate = this.eventStartDate;
      this.createLeagueWeekRequest.weekEndDate = this.eventEndDate;
    }

    this.applicationService.showGlobalSpinner();

    if (this.weekToEdit) {
      const updateRequest:UpdateLeagueWeekRequest = {
        id: this.weekToEdit.id,
        golfCourseName: this.createLeagueWeekRequest.golfCourseName,
        teeLocation: this.createLeagueWeekRequest.teeLocation,
        pinLocations: this.createLeagueWeekRequest.pinLocations,
        gimmeCircleSize: this.createLeagueWeekRequest.gimmeCircleSize,
        weekStartDate: this.createLeagueWeekRequest.weekStartDate,
        weekEndDate: this.createLeagueWeekRequest.weekEndDate,
        holesToPlay: this.createLeagueWeekRequest.holesToPlay,
        roundsToCount: this.createLeagueWeekRequest.roundsToCount,
        maxRoundsToPost: this.createLeagueWeekRequest.maxRoundsToPost,
        coursePar: this.createLeagueWeekRequest.coursePar
      };
      await this.golfEventService.updateLeagueWeek(updateRequest);
    } else {
      this.createLeagueWeekRequest.golfEventId = this.golfEventId!;
      await this.golfEventService.createLeagueWeek(this.createLeagueWeekRequest);
    }

    this.applicationService.hideGlobalSpinner();
    this.saved.emit();
  }

  cancel():void {
    this.canceled.emit();
  }

  get isLeagueWeek():boolean {
    return this.formatType !== EVENT_FORMAT.MULTI_COURSE
      && this.formatType !== EVENT_FORMAT.SINGLE_COURSE
      && this.formatType !== EVENT_FORMAT.HYBRID_SCRAMBLE;
  }

  get isEditing():boolean {
    return !!this.weekToEdit;
  }

  get formTitle():string {
    if (this.isEditing) {
      return this.isLeagueWeek ? 'Edit Week' : 'Edit Course';
    }
    return this.isLeagueWeek ? 'Create Week' : 'Add Course';
  }

  get saveLabel():string {
    if (this.isEditing) {
      return this.isLeagueWeek ? 'Save Week' : 'Save Course';
    }
    return this.isLeagueWeek ? 'Save Week' : 'Save Course';
  }

  emptyRequest():CreateLeagueWeekRequest {
    return {
      gimmeCircleSize: 12,
      golfCourseName: '',
      holesToPlay: 'FRONT9',
      pinLocations: 'Thursday',
      teeLocation: '',
      weekEndDate: this.getDefaultEndDate(),
      weekStartDate: this.getDefaultStartDate(),
      golfEventId: -1,
      roundsToCount: 1,
      maxRoundsToPost: 99,
      coursePar:36
    };
  }

  toInputDate(date:Date|string):string {
    if (typeof date === 'string') {
      return date.substring(0, 10);
    }
    return toCalendarControlFriendlyDate(date);
  }

  getDefaultStartDate():string {
    let dte = new Date();
    return toCalendarControlFriendlyDate(dte);
  }

  getDefaultEndDate():string {
    let dte = new Date();
    return toCalendarControlFriendlyDate(dte);
  }
}
